/**
 * HeadingStyleControls — PRP-MCP-15 scope addition (founder 2026-10-28): how section
 * headings ("Ceiling", "Walls", and headings from an imported estimate) are written in
 * the exported ESX: bold, underline, case, and a plain-text prefix and suffix, with a
 * live preview line. Used in Settings > Scope structure (the account default) and on a
 * company heading's edit form, where `inheritLabel` offers "Use my account style"
 * (value null). Contract: app/services/esx/heading_style.py.
 */
import type { HeadingStyle } from '../../types/headingStyle';
import { fullStyle } from '../../lib/headingsShape';
import { Input } from './ui/input';
import { Select } from './ui/select';
import { Switch } from './ui/switch';

export const BUILT_IN_STYLE: HeadingStyle = { bold: true, underline: true, case: 'as_typed', prefix: '', suffix: '' };
const MAX_WRAPPER = 10;

/** Mirrors heading_style.py: control characters and RTF braces/backslash are stripped, 10 characters max. */
function plain(text: string): string {
  // eslint-disable-next-line no-control-regex
  return text.replace(/[\x00-\x1f\x7f{}\\]/g, '').slice(0, MAX_WRAPPER);
}

export function styledHeading(text: string, s: HeadingStyle): string {
  const cased = s.case === 'upper' ? text.toUpperCase()
    : s.case === 'title' ? text.replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      : text;
  return `${s.prefix}${cased}${s.suffix}`;
}

interface Props {
  value: HeadingStyle | null | undefined;
  onChange: (_next: HeadingStyle | null) => void;
  /** Offer "inherit" (value null), e.g. "Use my account style". Omit for the account default. */
  inheritLabel?: string;
  idPrefix?: string;
}

export default function HeadingStyleControls({ value, onChange, inheritLabel, idPrefix = 'heading-style' }: Props) {
  const inherit = Boolean(inheritLabel) && !value;
  const style = fullStyle(value, BUILT_IN_STYLE);
  const set = (patch: Partial<HeadingStyle>) => onChange({ ...style, ...patch });

  return (
    <div className="space-y-3" data-testid={idPrefix}>
      {inheritLabel && (
        <label className="flex items-center gap-[6px] text-[12px] text-[var(--tx-lo)]">
          <Switch checked={inherit} data-testid={`${idPrefix}-inherit`}
            onCheckedChange={(on) => onChange(on ? null : { ...BUILT_IN_STYLE })} />
          {inheritLabel}
        </label>
      )}
      {!inherit && (
        <>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-[6px] text-[12px] text-[var(--tx-lo)]">
              <Switch checked={style.bold} onCheckedChange={(on) => set({ bold: on })} data-testid={`${idPrefix}-bold`} />
              Bold
            </label>
            <label className="flex items-center gap-[6px] text-[12px] text-[var(--tx-lo)]">
              <Switch checked={style.underline} onCheckedChange={(on) => set({ underline: on })}
                data-testid={`${idPrefix}-underline`} />
              Underline
            </label>
            <Select aria-label="Heading case" className="w-auto min-w-[150px]" value={style.case}
              data-testid={`${idPrefix}-case`}
              onChange={(e) => set({ case: e.target.value as HeadingStyle['case'] })}>
              <option value="as_typed">As typed</option>
              <option value="title">Title Case</option>
              <option value="upper">ALL CAPS</option>
            </Select>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Input aria-label="Before the heading" placeholder="Before, e.g. ***" className="w-[150px]"
              value={style.prefix} maxLength={MAX_WRAPPER} data-testid={`${idPrefix}-prefix`}
              onChange={(e) => set({ prefix: plain(e.target.value) })} />
            <Input aria-label="After the heading" placeholder="After, e.g. ***" className="w-[150px]"
              value={style.suffix} maxLength={MAX_WRAPPER} data-testid={`${idPrefix}-suffix`}
              onChange={(e) => set({ suffix: plain(e.target.value) })} />
          </div>
        </>
      )}
      <p className="m-0 text-[12px] text-[var(--tx-lo)]">
        Preview:{' '}
        <span data-testid={`${idPrefix}-preview`} className="text-[var(--tx-hi)] whitespace-pre"
          style={{ fontWeight: inherit || style.bold ? 700 : 400,
            textDecoration: inherit || style.underline ? 'underline' : 'none' }}>
          {inherit ? 'Ceiling' : styledHeading('Ceiling', style)}
        </span>
        {inherit && <span className="text-[var(--tx-faint)]"> (your account style applies)</span>}
      </p>
    </div>
  );
}
